import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { isBanquetUserRole } from "../model/banquetOwnerModel.js";
import { canAccessSuperAdmin } from "../services/banquetAccessRules.js";
import { loginBanquetOwner, logoutBanquetOwner, resetBanquetOwnerPassword } from "../services/banquetOwnerAuthService.js";

const inputClassName =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100";

const getPostLoginPath = (owner, isAdminLogin) => {
  if (isAdminLogin && canAccessSuperAdmin(owner)) {
    return "/super-admin/banquet-owners";
  }

  if (!isAdminLogin && owner?.isActive !== false && isBanquetUserRole(owner?.role)) {
    return "/banquet-owner/dashboard";
  }

  return "";
};

export default function BanquetOwnerLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const loginType = new URLSearchParams(location.search).get("type");
  const isAdminLogin = loginType === "admin";
  const pageTitle = isAdminLogin ? "Super Admin Login" : "Banquet Login";
  const [formState, setFormState] = useState({
    email: "",
    password: "",
  });
  const [status, setStatus] = useState({
    isSubmitting: false,
    error: "",
    info: "",
  });

  const updateField = (key, value) =>
    setFormState((current) => ({
      ...current,
      [key]: value,
    }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({
      isSubmitting: true,
      error: "",
      info: "",
    });

    try {
      const owner = await loginBanquetOwner(formState);
      const postLoginPath = getPostLoginPath(owner, isAdminLogin);

      if (!postLoginPath) {
        await logoutBanquetOwner();
        throw new Error(
          isAdminLogin
            ? "This login is only for super admin accounts."
            : "This login is only for banquet owner, manager, or staff accounts."
        );
      }

      navigate(postLoginPath, { replace: true });
    } catch (error) {
      setStatus({
        isSubmitting: false,
        error: error.message || "Unable to sign in right now.",
        info: "",
      });
      return;
    }

    setStatus((current) => ({
      ...current,
      isSubmitting: false,
    }));
  };

  const handleResetPassword = async () => {
    setStatus({
      isSubmitting: true,
      error: "",
      info: "",
    });

    try {
      await resetBanquetOwnerPassword({ email: formState.email });
      setStatus({
        isSubmitting: false,
        error: "",
        info: "Password reset flow will use Firebase Auth email reset once connected.",
      });
    } catch (error) {
      setStatus({
        isSubmitting: false,
        error: error.message || "Unable to start password reset.",
        info: "",
      });
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[linear-gradient(180deg,#eff6f1_0%,#f8fafc_100%)] px-3 py-6 sm:px-4 sm:py-10">
      <meta name="robots" content="noindex, nofollow" />
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center gap-3 text-sm text-slate-500">
          <Link to="/" className="hover:text-emerald-600">
            Home
          </Link>
          <span>/</span>
          <span className="text-slate-900">{pageTitle}</span>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1.05fr_0.95fr] lg:gap-6">
          <section className="rounded-[24px] bg-slate-950 p-5 text-white shadow-[0_30px_70px_-36px_rgba(15,23,42,0.9)] sm:rounded-[32px] sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-emerald-300">{isAdminLogin ? "Super Admin Access" : "Banquet Access"}</p>
            <h1 className="mt-4 text-2xl font-semibold leading-tight sm:text-4xl">
              {pageTitle} built around Firebase Auth
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-7 text-slate-300">
              This panel uses the same Firebase Auth login for banquet users and super admins. Final access is controlled by the user's Firestore profile role.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {[
                { label: "Role based", value: "Super admin, owner, manager, and staff" },
                { label: "App friendly", value: "Shared backend contracts for web and Flutter" },
                { label: "No fake auth", value: "No hardcoded owner login path" },
              ].map((item) => (
                <div key={item.label} className="rounded-3xl bg-white/10 p-4">
                  <p className="text-sm font-semibold text-emerald-300">{item.label}</p>
                  <p className="mt-2 text-sm text-slate-200">{item.value}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[32px] sm:p-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-600">Login</p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900">{pageTitle}</h2>
              <p className="mt-2 text-sm text-slate-600">
                Use your assigned DecorZoom account. Firebase/Auth profile rules decide the dashboard access after login.
              </p>
            </div>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Email
                </label>
                <input
                  type="email"
                  value={formState.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  className={inputClassName}
                  placeholder="owner@banquet.com"
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Password
                </label>
                <input
                  type="password"
                  value={formState.password}
                  onChange={(event) => updateField("password", event.target.value)}
                  className={inputClassName}
                  placeholder="Enter password"
                  autoComplete="current-password"
                />
              </div>

              {status.error ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {status.error}
                </div>
              ) : null}

              {status.info ? (
                <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700">
                  {status.info}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={status.isSubmitting}
                className="inline-flex w-full items-center justify-center rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status.isSubmitting ? "Signing in..." : "Sign In"}
              </button>

              <button
                type="button"
                disabled={status.isSubmitting}
                onClick={handleResetPassword}
                className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
              >
                Reset Password
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
